import { useEffect, useState } from 'react';

import Button from 'layout/atoms/Button';
import IconHint from 'layout/atoms/IconHint';
import TextEditable from 'layout/atoms/TextEditable';

import { AddTaskMultiPopup } from './AddTaskMultiPopup';

const AddTask = ({
    addTask = false,
    buttonText,
    color,
    placeholder,
    onTasksAdded,
    onAddingStateChange,
}: {
    addTask?: boolean;
    buttonText?: string;
    color?: string | null;
    placeholder: string;
    onTasksAdded: (tasks: string[]) => Promise<any>;
    onAddingStateChange?: (isAdding: boolean) => void;
}) => {
    const [userInput, setUserInput] = useState<string>('');
    const [isAddingTask, setIsAddingTask] = useState<boolean>(addTask || false);
    const [multiTasks, setMultiTasks] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        if (addTask) {
            setIsAddingTask(addTask);
        }
    }, [addTask]);

    const startAddingTask = () => {
        setIsAddingTask(true);
        onAddingStateChange?.(true);
    };

    const handleChange = (event: any) => {
        if (event.target.value.endsWith('\n')) {
            return;
        }
        setUserInput(event.target.value);
    };

    const handleBlur = () => {
        if (multiTasks.length === 0) {
            setIsAddingTask(false);
            onAddingStateChange?.(false);
        }
    };

    const onAddConfirmedButtonClicked = () => {
        return addingConfirmed();
    };

    const addingConfirmed = () => {
        if (!(userInput && userInput.trim()) || isSubmitting) {
            return;
        }

        if (multiTasks.length === 0) {
            const possibleTasks = userInput
                .split('\n')
                .map((t) => t.trim())
                .filter((t) => t.length > 0);

            if (possibleTasks.length > 1) {
                setMultiTasks(possibleTasks);
                return;
            }
        }

        setIsSubmitting(true);
        return onTasksAdded([userInput])
            .then(() => {
                setUserInput('');
                setIsSubmitting(false);
            })
            .catch(() => {
                setIsSubmitting(false);
            });
    };

    const addMultiTasks = () => {
        return onTasksAdded(multiTasks).then(() => {
            setMultiTasks([]);
            setUserInput('');
        });
    };

    const addConcatenatedTasks = () => {
        return onTasksAdded([userInput.trim()]).then(() => {
            setMultiTasks([]);
            setUserInput('');
        });
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        event.stopPropagation();
        if (event.key === 'Enter') {
            event.preventDefault();
            return addingConfirmed();
        } else if (event.key === 'Escape') {
            setIsAddingTask(false);
            onAddingStateChange?.(false);
        }
    };

    return (
        <>
            {!isAddingTask && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <Button
                        style={{
                            color: color || '',
                            fontWeight: '700',
                        }}
                        onClick={startAddingTask}
                    >
                        {buttonText || '+ NEW TASK'}{' '}
                        {userInput.trim() && (
                            <IconHint
                                variant="warning"
                                content="Unsaved draft. Finish editing and hit Enter to add."
                                onClick={startAddingTask}
                            />
                        )}
                    </Button>
                </div>
            )}
            {isAddingTask && (
                <>
                    <TextEditable
                        value={userInput}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        handleKeyDown={handleKeyDown}
                        maxLength={2048}
                        placeholder={placeholder}
                        onRenameConfirmedButtonClicked={
                            onAddConfirmedButtonClicked
                        }
                        cursorAtEnd
                    />
                    {multiTasks.length > 1 && (
                        <AddTaskMultiPopup
                            tasksCount={multiTasks.length}
                            addMultiTasks={addMultiTasks}
                            addConcatenatedTasks={addConcatenatedTasks}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default AddTask;
